/**
 * Parse an uploaded ontology (Turtle / N-Triples / TriG / N-Quads) into an
 * in-memory N3 store, plus fuzzy label indices for entity/property linking.
 * This is the local-source counterpart to Wikidata's `wbsearchentities`.
 */
import { DataFactory, Parser, Store } from "n3";
import Fuse from "fuse.js";
import { PrefixTable, WELL_KNOWN_PREFIXES } from "./prefixes.ts";

const { namedNode } = DataFactory;

const RDF_TYPE = "http://www.w3.org/1999/02/22-rdf-syntax-ns#type";
export const RDFS_LABEL = "http://www.w3.org/2000/01/rdf-schema#label";
const RDFS_COMMENT = "http://www.w3.org/2000/01/rdf-schema#comment";
const SKOS_PREF_LABEL = "http://www.w3.org/2004/02/skos/core#prefLabel";
const SKOS_ALT_LABEL = "http://www.w3.org/2004/02/skos/core#altLabel";
const PROPERTY_TYPES = [
  "http://www.w3.org/1999/02/22-rdf-syntax-ns#Property",
  "http://www.w3.org/2002/07/owl#ObjectProperty",
  "http://www.w3.org/2002/07/owl#DatatypeProperty",
  "http://www.w3.org/2002/07/owl#AnnotationProperty",
];

export type OntologyFormat = "Turtle" | "N-Triples" | "TriG" | "N-Quads";

export interface IndexEntry {
  iri: string;
  label: string;
  aliases: string[];
  description?: string;
  localName: string;
}

export class LabelIndex {
  readonly entries: IndexEntry[];
  private readonly fuse: Fuse<IndexEntry>;
  constructor(entries: IndexEntry[]) {
    this.entries = entries;
    this.fuse = new Fuse(entries, {
      keys: ["label", "aliases", "localName"],
      threshold: 0.42,
      ignoreLocation: true,
      includeScore: true,
    });
  }
  search(query: string, limit = 7): Array<{ entry: IndexEntry; score: number }> {
    const q = query.trim();
    if (!q) return [];
    return this.fuse.search(q, { limit }).map((r) => ({ entry: r.item, score: 1 - (r.score ?? 0) }));
  }
}

export interface OntologyStats {
  triples: number;
  entities: number;
  properties: number;
}

export interface LocalOntology {
  store: Store;
  prefixes: PrefixTable;
  entityIndex: LabelIndex;
  propertyIndex: LabelIndex;
  stats: OntologyStats;
}

function localName(iri: string): string {
  const m = iri.match(/[#/:]([^#/:]+)$/);
  return m?.[1] ?? iri;
}

/** Guess a format from a filename extension. */
export function formatFromFilename(name: string): OntologyFormat {
  const lower = name.toLowerCase();
  if (lower.endsWith(".nt")) return "N-Triples";
  if (lower.endsWith(".trig")) return "TriG";
  if (lower.endsWith(".nq")) return "N-Quads";
  return "Turtle"; // .ttl, .rdf, .owl, .n3, or unknown
}

export function parseOntology(text: string, format: OntologyFormat = "Turtle"): LocalOntology {
  const store = new Store();
  const prefixes = new PrefixTable(WELL_KNOWN_PREFIXES);
  const parser = new Parser({ format });
  let quads;
  try {
    quads = parser.parse(text, null, (prefix, node) => {
      if (prefix) prefixes.register(prefix, node.value);
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`Failed to parse ontology (${format}): ${msg}`);
  }
  store.addQuads(quads);

  const labelOf = (iri: string): { label: string; aliases: string[]; description?: string } => {
    const lits = (predicate: string) =>
      store
        .getObjects(namedNode(iri), namedNode(predicate), null)
        .filter((o) => o.termType === "Literal")
        .map((o) => o.value);
    const labels = [...lits(RDFS_LABEL), ...lits(SKOS_PREF_LABEL)];
    const aliases = lits(SKOS_ALT_LABEL);
    const desc = lits(RDFS_COMMENT)[0];
    const out: { label: string; aliases: string[]; description?: string } = {
      label: labels[0] ?? localName(iri),
      aliases,
    };
    if (desc !== undefined) out.description = desc;
    return out;
  };

  const isTypedAsAny = (iri: string, types: string[]): boolean =>
    types.some((t) => store.getQuads(namedNode(iri), namedNode(RDF_TYPE), namedNode(t), null).length > 0);

  // Properties = anything used as a predicate, plus anything typed as a property.
  const propertyIris = new Set<string>();
  for (const p of store.getPredicates(null, null, null)) {
    if (p.termType === "NamedNode" && p.value !== RDF_TYPE) propertyIris.add(p.value);
  }
  // Entities = named subjects that aren't properties (classes + individuals).
  const entityIris = new Set<string>();
  for (const s of store.getSubjects(null, null, null)) {
    if (s.termType !== "NamedNode") continue;
    if (isTypedAsAny(s.value, PROPERTY_TYPES)) {
      propertyIris.add(s.value);
    } else {
      entityIris.add(s.value);
    }
  }
  for (const iri of propertyIris) entityIris.delete(iri);

  const toEntry = (iri: string): IndexEntry => {
    const { label, aliases, description } = labelOf(iri);
    const entry: IndexEntry = { iri, label, aliases, localName: localName(iri) };
    if (description !== undefined) entry.description = description;
    return entry;
  };

  const entityIndex = new LabelIndex([...entityIris].map(toEntry));
  const propertyIndex = new LabelIndex([...propertyIris].map(toEntry));

  return {
    store,
    prefixes,
    entityIndex,
    propertyIndex,
    stats: { triples: store.size, entities: entityIris.size, properties: propertyIris.size },
  };
}
